/**
 * ForgotPassword — public page at /forgot-password.
 *
 * POST /api/auth/forgot-password { email } always returns 200 (no enumeration).
 * On submit we show a generic success state regardless of whether the email
 * exists — only the user with mailbox access sees the actual reset link.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Envelope, ArrowRight, ShieldCheck } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";

const HERO_INITIAL = { opacity: 0, y: 12 };
const HERO_ANIMATE = { opacity: 1, y: 0 };
const HERO_TRANSITION = { duration: 0.5 };

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    // Server ALWAYS returns 200; we show the same UI either way.
    try {
      await api.post("/auth/forgot-password", { email });
    } catch (err) {
      // Generic UX: don't surface the error to the user (anti-enumeration).
      // But log it for the admin/debug-tools console so we don't lose the signal.
      logger.warn("forgot-password request failed (silenced to user)", err?.response?.status, err?.message);
    } finally {
      setSubmitting(false);
      setDone(true);
    }
  };

  return (
    <div className="min-h-screen grain-bg grid grid-cols-1 lg:grid-cols-2">
      <div className="flex flex-col px-6 lg:px-16 py-10">
        <Link to="/login" className="flex items-center gap-2 w-fit">
          <div className="w-9 h-9 rounded-md bg-[var(--vs-secondary)] text-[var(--vs-bg)] grid place-items-center font-display font-bold">VS</div>
          <div className="leading-tight">
            <div className="font-display font-semibold">Vidya Seva</div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--vs-text-secondary)]">Password recovery</div>
          </div>
        </Link>

        <motion.div initial={HERO_INITIAL} animate={HERO_ANIMATE} transition={HERO_TRANSITION} className="my-auto max-w-md w-full">
          {!done ? (
            <>
              <div className="eyebrow mb-3">Reset by email</div>
              <h1 className="font-display text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.05]">
                Forgot your <span className="italic font-light text-[var(--vs-secondary)]">password</span>?
              </h1>
              <p className="mt-3 text-[var(--vs-text-secondary)]">Type your registered email below and we'll send a one-time reset link.</p>

              <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="forgot-form">
                <div>
                  <label className="block text-xs font-mono tracking-widest uppercase text-[var(--vs-secondary)] mb-1.5">Email</label>
                  <input
                    type="email" required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="forgot-email"
                    placeholder="you@example.org"
                    className="w-full px-4 py-3 rounded-md border border-[var(--vs-border)] bg-white focus:outline-none focus:border-[var(--vs-secondary)] focus:ring-2 focus:ring-[var(--vs-secondary)]/20"
                  />
                </div>
                <button
                  type="submit" disabled={submitting} data-testid="forgot-submit"
                  className="w-full px-6 py-3 rounded-md bg-[var(--vs-secondary)] text-[var(--vs-bg)] hover:bg-[var(--vs-secondary-hover)] transition-all disabled:opacity-60 inline-flex items-center justify-center gap-2 font-medium"
                >
                  {submitting ? "Sending…" : "Send reset link"}
                  {!submitting && <ArrowRight size={16} />}
                </button>
                <Link to="/login" className="block text-center text-xs underline underline-offset-4 text-[var(--vs-text-secondary)] hover:text-[var(--vs-secondary)]">
                  Back to sign in
                </Link>
              </form>
            </>
          ) : (
            <div data-testid="forgot-sent" className="space-y-4">
              <div className="w-12 h-12 rounded-md bg-[var(--vs-success)]/10 grid place-items-center">
                <Envelope size={22} weight="duotone" className="text-[var(--vs-success)]" />
              </div>
              <h1 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight leading-tight">Check your inbox</h1>
              <p className="text-[var(--vs-text-secondary)]">
                If <span className="font-mono text-[var(--vs-text)]">{email}</span> is registered, we sent a reset link. It expires in 15 minutes and can only be used once.
              </p>
              <p className="text-xs text-[var(--vs-text-secondary)]">Didn't get it? Check spam, or wait a minute and try again from the link below.</p>
              <div className="flex gap-3">
                <Link to="/login" className="text-sm underline underline-offset-4 text-[var(--vs-secondary)]">Back to sign in</Link>
                <button onClick={() => { setDone(false); setEmail(""); }} className="text-sm underline underline-offset-4 text-[var(--vs-text-secondary)]" data-testid="forgot-retry">Try a different email</button>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      <div className="hidden lg:flex bg-[var(--vs-bg-inverse)] text-[var(--vs-text-inverse)] flex-col px-12 py-10">
        <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--vs-primary)] mb-4">How this works</div>
        <h2 className="font-display text-2xl font-semibold tracking-tight leading-tight max-w-md">
          One-time-use, time-bound, and never tied to a single device.
        </h2>
        <ul className="mt-8 space-y-3 text-sm text-white/75 max-w-md">
          <li className="flex items-start gap-2"><ShieldCheck size={14} weight="fill" className="text-[var(--vs-primary)] mt-1 flex-shrink-0" /> Reset tokens are SHA-256 hashed before storage.</li>
          <li className="flex items-start gap-2"><ShieldCheck size={14} weight="fill" className="text-[var(--vs-primary)] mt-1 flex-shrink-0" /> Tokens self-destruct after 15 minutes or first use.</li>
          <li className="flex items-start gap-2"><ShieldCheck size={14} weight="fill" className="text-[var(--vs-primary)] mt-1 flex-shrink-0" /> Resetting your password invalidates every existing session.</li>
        </ul>
        <div className="mt-auto pt-8 text-xs font-mono text-white/50">AES-256-GCM at rest · TLS 1.2+ in transit</div>
      </div>
    </div>
  );
}
